import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { QrCode, Smartphone, BarChart3, ShieldCheck, Zap, Ticket, Users, CreditCard, ArrowRight, CheckCircle2, Globe, Trophy, Building2, PartyPopper, Calendar, Shield, MapPin, Heart, HandHeart, Star, Gift } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function SolucionesPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Add a slight delay to ensure rendering is complete
        setTimeout(() => {
          const yOffset = -100; // Account for fixed navbar
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden" data-id="element-743">
      <Navbar data-id="element-744" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-slate-950" data-id="element-745">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-746">
          <div className="absolute top-0 right-[10%] w-[50vw] h-[50vw] rounded-full bg-indigo-600/10 blur-[100px]" data-id="element-747" />
          <div className="absolute bottom-0 left-[10%] w-[40vw] h-[40vw] rounded-full bg-emerald-500/10 blur-[80px]" data-id="element-748" />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-749">
          <div className="max-w-4xl mx-auto text-center" data-id="element-750">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-751">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-900/30 border border-indigo-700/30 text-indigo-400 text-sm font-medium mb-6" data-id="element-752">
                <Zap size={14} data-id="element-753" />
                <span data-id="element-754">Ecosistema Completo</span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6" data-id="element-755">
                Soluciones diseñadas para <br data-id="element-756" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-emerald-400" data-id="element-757">
                  escalar tu negocio
                </span>
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-758">
                Desde eventos masivos y clubes de fútbol hasta asociaciones
                deportivas y pequeños eventos. Descubre cómo nuestras
                herramientas integradas optimizan cada etapa de tu operación sin
                importar el tamaño.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 1. EVENTOS MASIVOS */}
      <section id="eventos-masivos" className="py-24 bg-white relative scroll-mt-20" data-id="element-759">
        <div className="container mx-auto px-4 md:px-6" data-id="element-760">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-761">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-762">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-6" data-id="element-763">
                <Users className="w-8 h-8 text-indigo-600" data-id="element-764" />
              </div>
              <div className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full mb-4" data-id="element-765">
                Conciertos • Festivales • Deportes • Ferias
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-766">
                Eventos Masivos
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-767">
                Plataforma robusta preparada para soportar picos extremos de
                demanda. Gestiona miles de transacciones por minuto y controla
                el acceso de multitudes de forma fluida y segura.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-768">
                {['Venta masiva de entradas con alta concurrencia', 'Mapas de recintos interactivos', 'Control de aforo en tiempo real', 'Múltiples puntos de acceso simultáneos', 'Integración con pasarelas de pago locales e internacionales', 'Reportes financieros en tiempo real', 'Tickets dinámicos y transferencia inteligente', 'Personal y equipamiento de control de acceso', 'Gestión de ventas dentro del evento: administra tiendas, bares y restaurantes, teniendo control total del inventario, gestión de cobro y recaudación', 'Acreditación y control del personal del evento e invitados'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-769">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-770">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-771" />
                    </div>
                    <span className="text-slate-700" data-id="element-772">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2 bg-slate-100 rounded-3xl p-8 border border-slate-200" data-id="element-773">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-774">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-100" data-id="element-775">
                  <div className="font-semibold text-slate-900 flex items-center gap-2" data-id="element-776">
                    <Building2 className="w-5 h-5 text-indigo-500" data-id="element-777" />
                    Estadio - Aforo
                  </div>
                  <div className="text-emerald-500 font-medium flex items-center gap-1" data-id="element-778">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-779"></span>
                    En vivo
                  </div>
                </div>

                {/* Stadium Visualization */}
                <div className="relative w-full aspect-video bg-slate-50 rounded-lg border border-slate-100 mb-6 flex items-center justify-center overflow-hidden" data-id="element-780">
                  <div className="w-3/4 h-3/4 border-4 border-slate-200 rounded-[3rem] relative flex items-center justify-center" data-id="element-781">
                    <div className="w-1/2 h-1/2 bg-emerald-100 rounded-xl border border-emerald-200 flex items-center justify-center" data-id="element-782">
                      <span className="text-emerald-700 font-bold text-sm" data-id="element-783">
                        Cancha (95%)
                      </span>
                    </div>
                    {/* Tribunes */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-4 bg-indigo-500 rounded-full" data-id="element-784"></div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-1/2 h-4 bg-indigo-400 rounded-full" data-id="element-785"></div>
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-1/2 bg-indigo-600 rounded-full" data-id="element-786"></div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-4 h-1/2 bg-indigo-300 rounded-full" data-id="element-787"></div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-788">
                  {[{
                  name: 'Tribuna Norte',
                  sold: 100,
                  color: 'bg-indigo-500'
                }, {
                  name: 'Tribuna Sur',
                  sold: 85,
                  color: 'bg-indigo-400'
                }, {
                  name: 'Occidente VIP',
                  sold: 98,
                  color: 'bg-indigo-600'
                }].map((sector, i) => <div key={i} className="space-y-2" data-id="element-789">
                      <div className="flex justify-between text-sm" data-id="element-790">
                        <span className="text-slate-600 font-medium" data-id="element-791">
                          {sector.name}
                        </span>
                        <span className="font-bold text-slate-900" data-id="element-792">
                          {sector.sold}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-793">
                        <div className={`h-full ${sector.color} rounded-full`} style={{
                      width: `${sector.sold}%`
                    }} data-id="element-794" />
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. CLUBES DE FÚTBOL */}
      <section id="clubes-futbol" className="py-24 bg-slate-50 relative scroll-mt-20" data-id="element-795">
        <div className="container mx-auto px-4 md:px-6" data-id="element-796">
          <div className="flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-797">
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-798">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-6" data-id="element-799">
                <Ticket className="w-8 h-8 text-emerald-600" data-id="element-800" />
              </div>
              <div className="inline-block px-3 py-1 bg-white text-slate-600 text-sm font-medium rounded-full mb-4 border border-slate-200" data-id="element-801">
                Clubes Profesionales • Ligas Amateur
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-802">
                Clubes de Fútbol
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-803">
                Digitaliza la relación con tus hinchas. Una solución integral
                que combina la venta de entradas con la gestión de socios en una
                única aplicación con los colores de tu club.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-804">
                {['App y Portal Web institucional white-label del club; lo adaptamos a la ideología e imagen de cada uno', 'Gestión de socios y abonos', 'Venta de entradas por partido en la App, Web, Kioscos de Autoatención, WhatsApp, Boleterías y Redes Interconectadas', 'Wallet digital para socios e hinchas', 'Notificaciones push de partidos y eventos', 'Control de acceso al estadio', 'Dashboard de asistencia y recaudación', 'Integración con sistemas de membresía', 'Gestión de Tienda Oficial, muestra los productos y permite su compra', 'Gestión de acreditación y accesos a prensa', 'Gestión completa de escuelas de fútbol, administra sedes, profesores, alumnos, asistencia y el pago de cuotas'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-805">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-806">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-807" />
                    </div>
                    <span className="text-slate-700" data-id="element-808">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2 relative flex justify-center" data-id="element-809">
              {/* Phone Mockup */}
              <div className="relative border-slate-800 bg-slate-900 border-[12px] rounded-[2.5rem] h-[550px] w-[280px] shadow-2xl flex flex-col overflow-hidden" data-id="element-810">
                <div className="h-[32px] w-[3px] bg-slate-800 absolute -left-[15px] top-[72px] rounded-l-lg" data-id="element-811"></div>
                <div className="h-[46px] w-[3px] bg-slate-800 absolute -left-[15px] top-[124px] rounded-l-lg" data-id="element-812"></div>
                <div className="h-[64px] w-[3px] bg-slate-800 absolute -right-[15px] top-[142px] rounded-r-lg" data-id="element-813"></div>

                <div className="rounded-[2rem] overflow-hidden w-full h-full bg-slate-50 relative flex flex-col" data-id="element-814">
                  {/* App Header */}
                  <div className="bg-emerald-600 pt-12 pb-6 px-6 text-white rounded-b-3xl shadow-md z-10" data-id="element-815">
                    <div className="flex justify-between items-center mb-4" data-id="element-816">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-817">
                        <Shield className="w-6 h-6 text-white" data-id="element-818" />
                      </div>
                      <div className="bg-white/20 px-3 py-1 rounded-full text-xs font-medium" data-id="element-819">
                        Socio Activo
                      </div>
                    </div>
                    <h3 className="font-bold text-xl" data-id="element-820">Hola, Martín</h3>
                    <p className="text-emerald-100 text-sm" data-id="element-821">Socio N° 45892</p>
                  </div>

                  {/* App Content */}
                  <div className="flex-1 p-5 overflow-y-auto space-y-5 -mt-4 pt-8" data-id="element-822">
                    {/* Digital Carnet */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100" data-id="element-823">
                      <div className="flex justify-between items-center mb-3" data-id="element-824">
                        <span className="font-bold text-slate-800" data-id="element-825">
                          Tu Carnet Digital
                        </span>
                        <QrCode className="w-5 h-5 text-emerald-500" data-id="element-826" />
                      </div>
                      <div className="w-full h-24 bg-slate-100 rounded-xl flex items-center justify-center" data-id="element-827">
                        <QrCode className="w-16 h-16 text-slate-400" data-id="element-828" />
                      </div>
                    </div>

                    {/* Next Match */}
                    <div data-id="element-829">
                      <h4 className="font-bold text-slate-800 mb-3" data-id="element-830">
                        Próximo Partido
                      </h4>
                      <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100" data-id="element-831">
                        <div className="flex justify-between items-center mb-4" data-id="element-832">
                          <div className="flex flex-col items-center" data-id="element-833">
                            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mb-1" data-id="element-834">
                              <Shield className="w-5 h-5 text-emerald-600" data-id="element-835" />
                            </div>
                            <span className="text-xs font-bold" data-id="element-836">LOCAL</span>
                          </div>
                          <div className="flex flex-col items-center px-2" data-id="element-837">
                            <span className="text-xs text-slate-500 font-medium" data-id="element-838">
                              Sáb 15 Oct • 20:00
                            </span>
                            <span className="text-lg font-bold text-slate-800" data-id="element-839">
                              VS
                            </span>
                          </div>
                          <div className="flex flex-col items-center" data-id="element-840">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mb-1" data-id="element-841">
                              <Shield className="w-5 h-5 text-slate-400" data-id="element-842" />
                            </div>
                            <span className="text-xs font-bold text-slate-500" data-id="element-843">
                              VISITA
                            </span>
                          </div>
                        </div>
                        <Button variant="primary" className="w-full py-2 h-auto text-sm" data-id="element-844">
                          Comprar Entrada
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Nav */}
                  <div className="bg-white border-t border-slate-200 py-3 px-6 flex justify-between items-center" data-id="element-845">
                    <div className="flex flex-col items-center text-emerald-600" data-id="element-846">
                      <Shield className="w-6 h-6 mb-1" data-id="element-847" />
                      <span className="text-[10px] font-medium" data-id="element-848">Club</span>
                    </div>
                    <div className="flex flex-col items-center text-slate-400" data-id="element-849">
                      <Ticket className="w-6 h-6 mb-1" data-id="element-850" />
                      <span className="text-[10px] font-medium" data-id="element-851">Tickets</span>
                    </div>
                    <div className="flex flex-col items-center text-slate-400" data-id="element-852">
                      <Users className="w-6 h-6 mb-1" data-id="element-853" />
                      <span className="text-[10px] font-medium" data-id="element-854">Perfil</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-emerald-500/10 rounded-full blur-[80px] -z-10" data-id="element-855" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. ASOCIACIONES Y FEDERACIONES */}
      <section id="asociaciones" className="py-24 bg-white relative scroll-mt-20" data-id="element-856">
        <div className="container mx-auto px-4 md:px-6" data-id="element-857">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-858">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-859">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mb-6" data-id="element-860">
                <Globe className="w-8 h-8 text-purple-600" data-id="element-861" />
              </div>
              <div className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full mb-4" data-id="element-862">
                Federaciones • Asociaciones • Ligas
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-863">
                Asociaciones y Federaciones
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-864">
                Centraliza la gestión completa de la institución. Proveemos una
                plataforma tecnológica unificada y multicanal adaptada a la
                imagen y necesidades de cada organización.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-865">
                <li className="flex items-start gap-3" data-id="element-866">
                  <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-867">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-868" />
                  </div>
                  <div data-id="element-869">
                    <span className="text-slate-700 font-medium" data-id="element-870">
                      App y portal web institucional personalizado de la
                      federación o asociación
                    </span>
                    <ul className="mt-2 ml-1 space-y-2" data-id="element-871">
                      {['Permite la gestión y registro de socios y personas interesadas', 'Permite la administración de noticias y novedades', 'Dispone de un marketplace para la venta de productos de la organización', 'Permite la venta de entradas', 'Permite la venta de capacitaciones, talleres u otros', 'Permite el pago de cuotas u otros ítems de los socios', 'Permite el registro de participantes a torneos, eventos, capacitaciones, talleres u otros', 'Permite agregar opciones adicionales requeridas'].map((sub, j) => <li key={j} className="flex items-start gap-2 text-sm text-slate-600" data-id="element-872">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" data-id="element-873" />
                          {sub}
                        </li>)}
                    </ul>
                  </div>
                </li>
                {['Gestión centralizada de múltiples recintos', 'Venta de entradas para torneos y campeonatos', 'Credenciales digitales para deportistas y staff', 'Control de acceso multi-sede', 'Reportes consolidados por evento y sede', 'Gestión de acreditaciones', 'Integración con calendarios deportivos'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-874">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-875">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-876" />
                    </div>
                    <span className="text-slate-700" data-id="element-877">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2 bg-slate-950 rounded-3xl p-8 border border-slate-800 relative overflow-hidden" data-id="element-878">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-transparent" data-id="element-879" />

              <div className="relative z-10" data-id="element-880">
                <div className="flex items-center justify-between mb-8" data-id="element-881">
                  <div className="flex items-center gap-3" data-id="element-882">
                    <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center" data-id="element-883">
                      <Trophy className="w-5 h-5 text-white" data-id="element-884" />
                    </div>
                    <div data-id="element-885">
                      <h3 className="text-white font-bold" data-id="element-886">
                        Campeonato Nacional 2024
                      </h3>
                      <p className="text-slate-400 text-sm" data-id="element-887">
                        Panel de Control Multi-Sede
                      </p>
                    </div>
                  </div>
                  <div className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-xs font-medium border border-emerald-500/30" data-id="element-888">
                    Activo
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4" data-id="element-889">
                  {[{
                  name: 'Sede Central - Estadio Principal',
                  status: 'Alta demanda',
                  color: 'text-emerald-400',
                  bg: 'bg-emerald-400/20',
                  progress: 85
                }, {
                  name: 'Polideportivo Norte',
                  status: 'Normal',
                  color: 'text-indigo-400',
                  bg: 'bg-indigo-400/20',
                  progress: 45
                }, {
                  name: 'Complejo Deportivo Sur',
                  status: 'Casi lleno',
                  color: 'text-yellow-400',
                  bg: 'bg-yellow-400/20',
                  progress: 92
                }].map((venue, i) => <div key={i} className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 backdrop-blur-sm" data-id="element-890">
                      <div className="flex justify-between items-center mb-3" data-id="element-891">
                        <div className="flex items-center gap-2" data-id="element-892">
                          <MapPin className="w-4 h-4 text-slate-400" data-id="element-893" />
                          <span className="text-slate-200 font-medium text-sm" data-id="element-894">
                            {venue.name}
                          </span>
                        </div>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${venue.bg} ${venue.color}`} data-id="element-895">
                          {venue.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4" data-id="element-896">
                        <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden" data-id="element-897">
                          <div className="h-full bg-purple-500 rounded-full" style={{
                        width: `${venue.progress}%`
                      }} data-id="element-898"></div>
                        </div>
                        <span className="text-slate-400 text-xs font-medium w-8 text-right" data-id="element-899">
                          {venue.progress}%
                        </span>
                      </div>
                    </div>)}
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4" data-id="element-900">
                  <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 flex flex-col justify-center items-center text-center" data-id="element-901">
                    <Users className="w-6 h-6 text-purple-400 mb-2" data-id="element-902" />
                    <span className="text-2xl font-bold text-white" data-id="element-903">14.5K</span>
                    <span className="text-xs text-slate-400" data-id="element-904">
                      Acreditaciones
                    </span>
                  </div>
                  <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 flex flex-col justify-center items-center text-center" data-id="element-905">
                    <Ticket className="w-6 h-6 text-emerald-400 mb-2" data-id="element-906" />
                    <span className="text-2xl font-bold text-white" data-id="element-907">85.2K</span>
                    <span className="text-xs text-slate-400" data-id="element-908">
                      Tickets Vendidos
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PEQUEÑOS EVENTOS */}
      <section id="pequenos-eventos" className="py-24 bg-slate-50 relative scroll-mt-20" data-id="element-909">
        <div className="container mx-auto px-4 md:px-6" data-id="element-910">
          <div className="flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-911">
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-912">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mb-6" data-id="element-913">
                <PartyPopper className="w-8 h-8 text-indigo-600" data-id="element-914" />
              </div>
              <div className="inline-block px-3 py-1 bg-white text-slate-600 text-sm font-medium rounded-full mb-4 border border-slate-200" data-id="element-915">
                Teatro • Conferencias • Fiestas • Workshops
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-916">
                Pequeños Eventos
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-917">
                La solución perfecta para organizadores independientes. Crea tu
                evento en minutos, vende online sin complicaciones y controla el
                acceso usando solo tu smartphone.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-918">
                {['Setup rápido en minutos', 'Venta online sin complicaciones', 'Tickets con QR personalizados', 'Control de acceso con smartphone', 'Reportes simples y claros', 'Precios accesibles y escalables', 'Soporte dedicado', 'Página de venta personalizable'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-919">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-920">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-921" />
                    </div>
                    <span className="text-slate-700" data-id="element-922">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-923">
              <div className="relative max-w-sm mx-auto" data-id="element-924">
                {/* Ticket Mockup */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100 relative z-10 transform -rotate-3 hover:rotate-0 transition-transform duration-500" data-id="element-925">
                  <div className="h-32 bg-indigo-600 relative" data-id="element-926">
                    <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1514525253161-7a46d19cd819?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay" data-id="element-927"></div>
                    <div className="absolute bottom-4 left-6 text-white" data-id="element-928">
                      <div className="text-xs font-medium bg-white/20 px-2 py-1 rounded inline-block mb-2 backdrop-blur-sm" data-id="element-929">
                        Entrada General
                      </div>
                      <h3 className="text-xl font-bold leading-tight" data-id="element-930">
                        Workshop de
                        <br data-id="element-931" />
                        Innovación 2024
                      </h3>
                    </div>
                  </div>

                  {/* Ticket perforations */}
                  <div className="flex justify-between items-center -mt-3 relative z-20" data-id="element-932">
                    <div className="w-6 h-6 bg-slate-50 rounded-full -ml-3 shadow-inner" data-id="element-933"></div>
                    <div className="flex-1 border-t-2 border-dashed border-slate-200 mx-2" data-id="element-934"></div>
                    <div className="w-6 h-6 bg-slate-50 rounded-full -mr-3 shadow-inner" data-id="element-935"></div>
                  </div>

                  <div className="p-6 pt-4" data-id="element-936">
                    <div className="flex justify-between items-start mb-6" data-id="element-937">
                      <div data-id="element-938">
                        <div className="text-xs text-slate-500 font-medium mb-1" data-id="element-939">
                          FECHA
                        </div>
                        <div className="font-bold text-slate-800 flex items-center gap-1" data-id="element-940">
                          <Calendar className="w-4 h-4 text-indigo-500" data-id="element-941" />
                          24 Nov, 2024
                        </div>
                      </div>
                      <div className="text-right" data-id="element-942">
                        <div className="text-xs text-slate-500 font-medium mb-1" data-id="element-943">
                          HORA
                        </div>
                        <div className="font-bold text-slate-800" data-id="element-944">
                          18:00 hrs
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-center mb-4" data-id="element-945">
                      <div className="p-3 bg-white border-2 border-slate-100 rounded-xl shadow-sm" data-id="element-946">
                        <QrCode className="w-32 h-32 text-slate-800" data-id="element-947" />
                      </div>
                    </div>

                    <div className="text-center text-sm text-slate-500 font-medium" data-id="element-948">
                      ID: TKM-8492-X7
                    </div>
                  </div>
                </div>

                {/* Background decorative elements */}
                <div className="absolute top-10 -right-10 w-24 h-24 bg-emerald-400/20 rounded-full blur-xl -z-10" data-id="element-949"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-indigo-400/20 rounded-full blur-xl -z-10" data-id="element-950"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. ORGANIZACIONES SIN FINES DE LUCRO */}
      <section id="sin-fines-de-lucro" className="py-24 bg-white relative scroll-mt-20" data-id="element-951">
        <div className="container mx-auto px-4 md:px-6" data-id="element-952">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-953">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-954">
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mb-6" data-id="element-955">
                <Heart className="w-8 h-8 text-purple-600" data-id="element-956" />
              </div>
              <div className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full mb-4" data-id="element-957">
                ONGs • Fundaciones • Instituciones Educativas • Beneficencia
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-958">
                Organizaciones sin Fines de Lucro
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-959">
                Potencia tus eventos solidarios y de recaudación con
                herramientas pensadas para maximizar el impacto social. Precios
                especiales y funcionalidades adaptadas a las necesidades de
                organizaciones que buscan hacer la diferencia.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-960">
                {['Venta de entradas para eventos benéficos', 'Gestión de donaciones integrada', 'Credenciales para voluntarios y staff', 'Reportes de recaudación transparentes', 'Página de evento personalizable con tu causa', 'Precios especiales para organizaciones sin fines de lucro', 'Integración con plataformas de donación', 'Comunicación directa con asistentes y donantes'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-961">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-962">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-963" />
                    </div>
                    <span className="text-slate-700" data-id="element-964">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-965">
              <div className="bg-slate-950 rounded-3xl p-8 border border-slate-800 relative overflow-hidden" data-id="element-966">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-transparent" data-id="element-967" />

                <div className="relative z-10" data-id="element-968">
                  <div className="flex items-center gap-3 mb-8" data-id="element-969">
                    <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center" data-id="element-970">
                      <HandHeart className="w-5 h-5 text-white" data-id="element-971" />
                    </div>
                    <div data-id="element-972">
                      <h3 className="text-white font-bold" data-id="element-973">
                        Gala Benéfica 2024
                      </h3>
                      <p className="text-slate-400 text-sm" data-id="element-974">
                        Panel de Recaudación
                      </p>
                    </div>
                  </div>

                  {/* Donation Progress */}
                  <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6 mb-4" data-id="element-975">
                    <div className="text-center mb-4" data-id="element-976">
                      <div className="text-slate-400 text-sm mb-1" data-id="element-977">
                        Meta de Recaudación
                      </div>
                      <div className="text-3xl font-bold text-white" data-id="element-978">
                        $45,200{' '}
                        <span className="text-lg text-slate-500" data-id="element-979">
                          / $50,000
                        </span>
                      </div>
                    </div>
                    <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden mb-2" data-id="element-980">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-emerald-400 rounded-full" style={{
                      width: '90%'
                    }} data-id="element-981"></div>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400" data-id="element-982">
                      <span data-id="element-983">90% completado</span>
                      <span data-id="element-984">$4,800 restantes</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3" data-id="element-985">
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-986">
                      <Ticket className="w-5 h-5 text-emerald-400 mx-auto mb-2" data-id="element-987" />
                      <span className="text-xl font-bold text-white block" data-id="element-988">
                        320
                      </span>
                      <span className="text-[11px] text-slate-400" data-id="element-989">
                        Entradas
                      </span>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-990">
                      <Heart className="w-5 h-5 text-purple-400 mx-auto mb-2" data-id="element-991" />
                      <span className="text-xl font-bold text-white block" data-id="element-992">
                        85
                      </span>
                      <span className="text-[11px] text-slate-400" data-id="element-993">
                        Donaciones
                      </span>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-994">
                      <Users className="w-5 h-5 text-indigo-400 mx-auto mb-2" data-id="element-995" />
                      <span className="text-xl font-bold text-white block" data-id="element-996">
                        42
                      </span>
                      <span className="text-[11px] text-slate-400" data-id="element-997">
                        Voluntarios
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROGRAMAS DE FIDELIZACIÓN SECTION */}
      <section id="fidelizacion" className="py-24 bg-slate-50 relative scroll-mt-20" data-id="element-998">
        <div className="container mx-auto px-4 md:px-6" data-id="element-999">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-1000">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-1001">
              <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-6" data-id="element-1002">
                <Star className="w-8 h-8 text-amber-600" data-id="element-1003" />
              </div>
              <div className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full mb-4" data-id="element-1004">
                Puntos • Beneficios • Recompensas • Engagement
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1005">
                Programas de Fidelización
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-1006">
                Construye relaciones duraderas con tu audiencia a través de
                programas de lealtad inteligentes. Recompensa la asistencia, las
                compras y la participación para convertir asistentes ocasionales
                en fans comprometidos.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1007">
                {['Acumulación de puntos por compras y asistencia', 'Catálogo de recompensas y beneficios canjeables', 'Niveles de membresía con ventajas exclusivas', 'Descuentos y preventas especiales para miembros', 'Comunicación segmentada según nivel de fidelidad', 'Dashboard de métricas de engagement y retención', 'Integración con la app móvil y wallet digital', 'Gamificación y desafíos para incentivar la participación'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1008">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-1009">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-1010" />
                    </div>
                    <span className="text-slate-700" data-id="element-1011">{feature}</span>
                  </li>)}
              </ul>
              <a href="https://benefika.net/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-6 py-3 bg-white hover:bg-orange-50 text-slate-900 border-2 border-orange-500 rounded-xl transition-colors font-medium text-sm group" data-id="element-1012">
                <img src="https://cdn.magicpatterns.com/uploads/rGwNdcUQBXjqXzrggvXeFv/logo_benefica_positivo.png" alt="Benefika" className="h-5 w-auto" data-id="element-1013" />
                <span data-id="element-1014">
                  ¿Quieres saber más? Ir a <strong data-id="element-1015">Benefika</strong>, nuestra
                  plataforma de fidelización
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1016" />
              </a>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-1017">
              <div className="bg-slate-950 rounded-3xl p-8 border border-slate-800 relative overflow-hidden" data-id="element-1018">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 to-transparent" data-id="element-1019" />

                <div className="relative z-10" data-id="element-1020">
                  <div className="flex items-center gap-3 mb-8" data-id="element-1021">
                    <img src="https://cdn.magicpatterns.com/uploads/rGwNdcUQBXjqXzrggvXeFv/logo_benefica_positivo.png" alt="Benefika" className="h-8 w-auto brightness-0 invert" data-id="element-1022" />
                    <div data-id="element-1023">
                      <h3 className="text-white font-bold" data-id="element-1024">
                        Club de Fidelidad
                      </h3>
                      <p className="text-slate-400 text-sm" data-id="element-1025">Panel de Miembro</p>
                    </div>
                  </div>

                  {/* Points Balance */}
                  <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6 mb-4" data-id="element-1026">
                    <div className="text-center mb-4" data-id="element-1027">
                      <div className="text-slate-400 text-sm mb-1" data-id="element-1028">
                        Puntos Acumulados
                      </div>
                      <div className="text-3xl font-bold text-white" data-id="element-1029">
                        12,450{' '}
                        <span className="text-lg text-amber-400" data-id="element-1030">pts</span>
                      </div>
                    </div>
                    <div className="h-3 w-full bg-slate-800 rounded-full overflow-hidden mb-2" data-id="element-1031">
                      <div className="h-full bg-gradient-to-r from-amber-500 to-emerald-400 rounded-full" style={{
                      width: '75%'
                    }} data-id="element-1032"></div>
                    </div>
                    <div className="flex justify-between text-xs text-slate-400" data-id="element-1033">
                      <span data-id="element-1034">Nivel Gold</span>
                      <span data-id="element-1035">2,550 pts para Platinum</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3" data-id="element-1036">
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-1037">
                      <Gift className="w-5 h-5 text-amber-400 mx-auto mb-2" data-id="element-1038" />
                      <span className="text-xl font-bold text-white block" data-id="element-1039">
                        8
                      </span>
                      <span className="text-[11px] text-slate-400" data-id="element-1040">Canjes</span>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-1041">
                      <Ticket className="w-5 h-5 text-emerald-400 mx-auto mb-2" data-id="element-1042" />
                      <span className="text-xl font-bold text-white block" data-id="element-1043">
                        24
                      </span>
                      <span className="text-[11px] text-slate-400" data-id="element-1044">
                        Eventos
                      </span>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-1045">
                      <Star className="w-5 h-5 text-amber-400 mx-auto mb-2" data-id="element-1046" />
                      <span className="text-xl font-bold text-white block" data-id="element-1047">
                        Gold
                      </span>
                      <span className="text-[11px] text-slate-400" data-id="element-1048">Nivel</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTROL DE ACCESOS Y VISITAS SECTION */}
      <section id="control-accesos" className="py-24 bg-white relative scroll-mt-20" data-id="element-1049">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1050">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-1051">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-1052">
              <div className="w-16 h-16 rounded-2xl bg-cyan-100 flex items-center justify-center mb-6" data-id="element-1053">
                <Building2 className="w-8 h-8 text-cyan-600" data-id="element-1054" />
              </div>
              <div className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full mb-4" data-id="element-1055">
                Edificios • Condominios • Ciudadelas • Instituciones
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1056">
                Control de Accesos y Visitas
              </h2>
              <p className="text-slate-600 text-lg mb-6 leading-relaxed" data-id="element-1057">
                Gestiona y controla los accesos y visitas en edificios de
                oficinas, departamentos, condominios, ciudadelas e
                instituciones. Registro digital de visitantes, control de
                ingreso y salida, y reportes en tiempo real para una seguridad
                integral.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1058">
                {['Registro digital de visitantes con datos y fotografía', 'Control de ingreso y salida en tiempo real', 'Pre-autorización de visitas por residentes u oficinas', 'Notificaciones automáticas al anfitrión', 'Historial completo de accesos y visitas', 'Gestión de accesos para proveedores y personal de servicio', 'Reportes de seguridad y tráfico de personas', 'Integración con sistemas de control de acceso físico'].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1059">
                    <div className="mt-1 bg-emerald-100 p-1 rounded-full shrink-0" data-id="element-1060">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-1061" />
                    </div>
                    <span className="text-slate-700" data-id="element-1062">{feature}</span>
                  </li>)}
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="md:w-1/2" data-id="element-1063">
              <div className="bg-slate-950 rounded-3xl p-8 border border-slate-800 relative overflow-hidden" data-id="element-1064">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-transparent" data-id="element-1065" />

                <div className="relative z-10" data-id="element-1066">
                  <div className="flex items-center gap-3 mb-8" data-id="element-1067">
                    <div className="w-10 h-10 bg-cyan-600 rounded-lg flex items-center justify-center" data-id="element-1068">
                      <Shield className="w-5 h-5 text-white" data-id="element-1069" />
                    </div>
                    <div data-id="element-1070">
                      <h3 className="text-white font-bold" data-id="element-1071">
                        Torre Empresarial
                      </h3>
                      <p className="text-slate-400 text-sm" data-id="element-1072">
                        Panel de Control de Accesos
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-6" data-id="element-1073">
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-1074">
                      <span className="text-2xl font-bold text-white block mb-1" data-id="element-1075">
                        47
                      </span>
                      <span className="text-[11px] text-slate-400 uppercase tracking-wider" data-id="element-1076">
                        Visitas Hoy
                      </span>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-1077">
                      <span className="text-2xl font-bold text-cyan-400 block mb-1" data-id="element-1078">
                        12
                      </span>
                      <span className="text-[11px] text-slate-400 uppercase tracking-wider" data-id="element-1079">
                        En Edificio
                      </span>
                    </div>
                    <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-4 text-center" data-id="element-1080">
                      <span className="text-2xl font-bold text-emerald-400 block mb-1" data-id="element-1081">
                        8
                      </span>
                      <span className="text-[11px] text-slate-400 uppercase tracking-wider" data-id="element-1082">
                        Pre-autorizados
                      </span>
                    </div>
                  </div>

                  <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-5" data-id="element-1083">
                    <h4 className="text-sm font-medium text-white mb-4" data-id="element-1084">
                      Últimos Accesos
                    </h4>
                    <div className="space-y-4" data-id="element-1085">
                      <div className="flex items-center justify-between" data-id="element-1086">
                        <div className="flex items-center gap-3" data-id="element-1087">
                          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 text-xs font-medium" data-id="element-1088">
                            CM
                          </div>
                          <div data-id="element-1089">
                            <div className="text-sm text-white font-medium" data-id="element-1090">
                              Carlos Mendoza
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-1091">
                              Oficina 1803 • 09:15
                            </div>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-medium rounded-full border border-emerald-500/20" data-id="element-1092">
                          INGRESO
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1093">
                        <div className="flex items-center gap-3" data-id="element-1094">
                          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 text-xs font-medium" data-id="element-1095">
                            ML
                          </div>
                          <div data-id="element-1096">
                            <div className="text-sm text-white font-medium" data-id="element-1097">
                              María López
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-1098">
                              Depto. 502 • 09:32
                            </div>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-medium rounded-full border border-emerald-500/20" data-id="element-1099">
                          INGRESO
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1100">
                        <div className="flex items-center gap-3" data-id="element-1101">
                          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 text-xs font-medium" data-id="element-1102">
                            PR
                          </div>
                          <div data-id="element-1103">
                            <div className="text-sm text-white font-medium" data-id="element-1104">
                              Pedro Ruiz
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-1105">
                              Oficina 901 • 09:10
                            </div>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-rose-500/20 text-rose-400 text-[10px] font-medium rounded-full border border-rose-500/20" data-id="element-1106">
                          SALIDA
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-slate-50" data-id="element-1107">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1108">
          <div className="bg-slate-950 rounded-3xl p-8 md:p-16 text-center relative overflow-hidden" data-id="element-1109">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0" data-id="element-1110">
              <div className="absolute -top-[50%] -left-[20%] w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[120px]" data-id="element-1111" />
            </div>

            <div className="relative z-10 max-w-3xl mx-auto" data-id="element-1112">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1113">
                Transforma tu gestión hoy
              </h2>
              <p className="text-slate-400 text-lg mb-10" data-id="element-1114">
                Agenda una demostración personalizada y descubre cómo
                TicketMaestro puede adaptarse a tus necesidades específicas.
              </p>
              <Button variant="primary" size="lg" onClick={() => window.location.href = 'mailto:contacto@ticketmaestro.net'} data-id="element-1115">
                Contactar Ventas
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-1116" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-1117" />
    </div>;
}